import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import styles from './Select.module.css';

import useTooltip from '../../../hooks/useTooltip';
import useClickOutside from '../../../hooks/useClickOutside';

import SelectorSimple from '../../Helpers/SelectorSimple/SelectorSimple';

import { SelectorProps } from '../../../interfaces/Input';
import OptionButtonSimple from '../../Helpers/OptionButtonSimple/OptionButtonSimple';

interface Props<T> {
    value?: string
    selector?: (props: SelectorProps) => React.ReactNode
    options: ReadonlyArray<T>
    getOptionLabel?: (option: T) => React.ReactNode
    renderOption?: (props: React.HTMLAttributes<HTMLButtonElement>, option: T) => React.ReactNode
    onSelect: (option: T) => void
}

const Select = <T,>({
    value = '',
    selector,
    options,
    onSelect,
    getOptionLabel: getOptionLabelProp,
    renderOption
}: Props<NonNullable<T>>) => {

    const [showOptions, setShowOptions] = useState(false);

    const setFalse = () => setShowOptions(false);

    const handleClickSelect = () => {
        setShowOptions(isShowOptions => !isShowOptions);
    };

    const handleClickOption = (option: NonNullable<T>) => () => {
        setShowOptions(false);
        onSelect(option);
    };

    const getStylePos = (pos: DOMRect) => ({ top: `${pos.bottom + 5}px`, left: `${pos.left}px`, width: pos.width });

    const { tooltipRef, getStyles } = useTooltip(getStylePos);

    const wrapperRef = useClickOutside(setFalse, tooltipRef);

    const defaultGetOptionLabel = (option: NonNullable<T>) => {
        if (typeof option !== 'object') return String(option);
        if ('label' in option) return (option as { label: string }).label;
        throw Error('Option must be either a primitive data type or an object with a label. Otherwise use the "getOptionLabel" or the "renderOption" Prop.');
    };
    const getOptionLabel = getOptionLabelProp ?? defaultGetOptionLabel;

    const defaultKeyOption = (option: NonNullable<T>): React.Key => {
        if (typeof option !== 'object') return String(option);
        if ('id' in option) return (option as { id: string | number }).id;
        throw Error('Option must be either a React.Key or an object with an id. Otherwise use the "renderOption" prop.');
    };

    const defaultRenderSelector = (props: SelectorProps): React.ReactNode =>
        <SelectorSimple {...props} />;

    const renderSelector = selector ?? defaultRenderSelector;

    const defaultRenderOption = (props: React.HTMLAttributes<HTMLButtonElement>, option: NonNullable<T>): React.ReactNode =>
        <OptionButtonSimple
            {...props}
            label={getOptionLabel(option)}
            key={defaultKeyOption(option)}
        />;

    const renderedOption = (option: NonNullable<T>) => {
        const props: React.HTMLAttributes<HTMLButtonElement> = {
            onClick: handleClickOption(option)
        };
        return renderOption ? renderOption(props, option) : defaultRenderOption(props, option);
    };

    return (
        <div ref={tooltipRef}>
            <div>
                <button onClick={handleClickSelect} className={styles.buttonWrapper}>
                    {renderSelector({ value, showOptions })}
                </button>
                {showOptions && ReactDOM.createPortal(
                    <div
                        className={styles.optionsContainer}
                        style={{
                            ...getStyles(),
                        }}
                        ref={wrapperRef}
                    >
                        {options.map(option =>
                            renderedOption(option)
                        )}
                    </div>, document.getElementById('tooltip') as HTMLElement,
                )}
            </div>
        </div>
    );
};

export default Select;